"use client";

import { Button } from "@workspace/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@workspace/ui/components/command";
import { Label } from "@workspace/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { ArrowLeftIcon, ArrowRightIcon, FilterIcon } from "lucide-react";
import { type SetValues, useQueryStates, type Values } from "nuqs";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import {
  countActiveMarketplaceFilters,
  hasActiveMarketplaceFilters,
} from "./marketplace-active-filters";
import {
  type AiFilterResult,
  MarketplaceAiFilterInput,
} from "./marketplace-ai-filter-input";
import { marketplaceFilterFields } from "./marketplace-filter-constants";
import {
  CheckboxFilter,
  DateRangeFilter,
  InputFilter,
  SliderFilter,
} from "./marketplace-filter-controls";
import { marketplaceSearchParamsParser } from "./marketplace-search-params";
import { useAiFilterMap } from "./use-ai-filter-map";

type SelectedFilterField = {
  value: string | number | boolean;
  label: string;
  type: string;
  options?: Array<{ label: string; value: unknown }>;
  min?: number;
  max?: number;
  leading?: string;
  trailing?: string;
};

function FilterDetailPanel({
  selectedField,
  searchParams,
  setSearchParams,
  userLatitude,
  userLongitude,
  onBack,
  getFieldValue,
  resetField,
}: {
  selectedField: SelectedFilterField;
  searchParams: Values<typeof marketplaceSearchParamsParser>;
  setSearchParams: SetValues<typeof marketplaceSearchParamsParser>;
  userLatitude?: number;
  userLongitude?: number;
  onBack: () => void;
  getFieldValue: (field: string) => unknown;
  resetField: (field: string) => void;
}) {
  const { moveMap, handleNearMe } = useAiFilterMap();

  const handleSliderChange = (field: string, value: number[]) => {
    if (field === "compensation") {
      setSearchParams({
        ...searchParams,
        compensation: value,
      });
    }
  };

  const handleCheckboxChange = (field: string, values: string[]) => {
    setSearchParams({
      ...searchParams,
      [field]: values,
    });
  };

  const handleDateRangeChange = (
    dateRange: { from?: Date; to?: Date } | undefined
  ) => {
    if (dateRange?.from && dateRange?.to) {
      setSearchParams({
        ...searchParams,
        posted_date: [dateRange.from, dateRange.to],
      });
    } else {
      setSearchParams({
        ...searchParams,
        posted_date: null,
      });
    }
  };

  const handleAiFiltersGenerated = (result: AiFilterResult) => {
    setSearchParams({
      ...searchParams,
      ...result.filters,
    });

    moveMap(result.map);
    handleNearMe(result.location);
  };

  const hasActiveFilters = () => hasActiveMarketplaceFilters(searchParams);

  return (
    <div>
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <button
          aria-label="Back to filter list"
          className="mr-2 flex size-7 items-center justify-center rounded hover:bg-accent"
          onClick={onBack}
          type="button"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <span className="font-medium text-sm">{selectedField.label}</span>
        <div className="ml-auto">
          {selectedField.value === "ai-filters" ? (
            hasActiveFilters() && (
              <button
                className="text-muted-foreground text-xs hover:text-foreground"
                onClick={() => resetField("ai-filters")}
                type="button"
              >
                Clear All
              </button>
            )
          ) : (
            <button
              className="text-muted-foreground text-xs hover:text-foreground"
              onClick={() => resetField(selectedField.value as string)}
              type="button"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-2">
        {(() => {
          if (selectedField.value === "ai-filters") {
            return (
              <div className="space-y-3">
                <Label className="font-medium text-sm">
                  Describe the jobs you're looking for
                </Label>
                <MarketplaceAiFilterInput
                  onFiltersGenerated={handleAiFiltersGenerated}
                  userLatitude={userLatitude}
                  userLongitude={userLongitude}
                />
              </div>
            );
          }

          const fieldValue = getFieldValue(selectedField.value as string);

          switch (selectedField.type) {
            case "checkbox": {
              return (
                <CheckboxFilter
                  field={
                    selectedField as {
                      options?: Array<{ label: string; value: unknown }>;
                      value: unknown;
                    }
                  }
                  onChange={(values) =>
                    handleCheckboxChange(selectedField.value as string, values)
                  }
                  value={fieldValue as string[]}
                />
              );
            }
            case "slider": {
              return (
                <SliderFilter
                  field={
                    selectedField as {
                      min: number;
                      max: number;
                      value: unknown;
                      trailing?: string;
                      leading?: string;
                    }
                  }
                  onChange={(values) =>
                    handleSliderChange(selectedField.value as string, values)
                  }
                  value={fieldValue as number[]}
                />
              );
            }
            case "input": {
              return (
                <InputFilter
                  field={selectedField as { value: unknown }}
                  onChange={(value) => {
                    setSearchParams({
                      ...searchParams,
                      [selectedField.value as string]: value,
                    });
                  }}
                  value={fieldValue as string}
                />
              );
            }
            case "timerange": {
              return (
                <DateRangeFilter
                  onChange={handleDateRangeChange}
                  value={fieldValue as DateRange | undefined}
                />
              );
            }
            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
}

function FilterList({
  filterFields,
  searchParams,
  getFieldValue,
  resetField,
  onSelect,
}: {
  filterFields: typeof marketplaceFilterFields;
  searchParams: Values<typeof marketplaceSearchParamsParser>;
  getFieldValue: (field: string) => unknown;
  resetField: (field: string) => void;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="divide-y">
      <Command>
        <CommandInput
          aria-label="Search filters"
          placeholder="Search filters..."
        />
        <CommandEmpty>No filters found.</CommandEmpty>
        <CommandList>
          <CommandGroup>
            <CommandItem
              className="group"
              key="ai-filters"
              onSelect={() => onSelect("ai-filters")}
              value="Natural language filters"
            >
              <div className="flex w-full items-center justify-between">
                <span className="truncate font-medium text-sm">
                  Natural language filters
                </span>
                <ArrowRightIcon className="size-4 opacity-0 group-aria-selected:opacity-100" />
              </div>
            </CommandItem>
            {filterFields.map((field) => {
              const _fieldValue = getFieldValue(field.value as string);

              // Special handling for compensation slider
              let hasValue = false;
              if (field.value === "compensation") {
                hasValue =
                  searchParams.compensation !== null &&
                  searchParams.compensation.length > 0;
              } else if (field.value === "posted_date") {
                hasValue =
                  searchParams.posted_date !== null &&
                  searchParams.posted_date.length === 2;
              } else {
                const searchParamValue =
                  searchParams[field.value as keyof typeof searchParams];
                hasValue =
                  searchParamValue !== null && searchParamValue !== undefined;
                if (hasValue && Array.isArray(searchParamValue)) {
                  hasValue = searchParamValue.length > 0;
                }
              }

              return (
                <CommandItem
                  className="group"
                  key={String(field.value)}
                  onSelect={() => onSelect(field.value as string)}
                  value={field.label}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="truncate font-medium text-sm">
                      {field.label}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      {hasValue && (
                        <button
                          className="text-muted-foreground text-xs hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            resetField(field.value as string);
                          }}
                          type="button"
                        >
                          Clear
                        </button>
                      )}
                      <ArrowRightIcon className="size-4 opacity-0 group-aria-selected:opacity-100" />
                    </div>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}

export function MarketplaceFilterControlsPopover({
  userLatitude,
  userLongitude,
}: {
  userLatitude?: number;
  userLongitude?: number;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useQueryStates(
    marketplaceSearchParamsParser
  );
  const filterFields = marketplaceFilterFields;
  const activeFilterCount = countActiveMarketplaceFilters(searchParams);

  const selectedField =
    selected === "ai-filters"
      ? { value: "ai-filters", label: "Natural language filters", type: "ai" }
      : filterFields.find((f) => (f.value as string) === selected);

  const resetField = (field: string) => {
    const resetValues: Record<string, unknown> = { ...searchParams };

    switch (field) {
      case "compensation": {
        resetValues.compensation = null;
        break;
      }
      case "compensation_rate": {
        resetValues.compensation_rate = null;
        break;
      }
      case "category": {
        resetValues.category = null;
        break;
      }
      case "work_model": {
        resetValues.work_model = null;
        break;
      }
      case "commitment": {
        resetValues.commitment = null;
        break;
      }
      case "priority": {
        resetValues.priority = null;
        break;
      }
      case "resume": {
        resetValues.resume = null;
        break;
      }
      case "posted_date": {
        resetValues.posted_date = null;
        break;
      }
      case "ai-filters": {
        resetValues.compensation = null;
        resetValues.compensation_rate = null;
        resetValues.category = null;
        resetValues.work_model = null;
        resetValues.commitment = null;
        resetValues.priority = null;
        resetValues.resume = null;
        resetValues.posted_date = null;
        resetValues.geoFilter = null;
        break;
      }
      default: {
        resetValues[field] = null;
        break;
      }
    }

    setSearchParams(resetValues);
  };

  const getFieldValue = (field: string): unknown => {
    switch (field) {
      case "compensation": {
        // Return default range when filters are null, otherwise return current values
        if (
          !searchParams.compensation ||
          searchParams.compensation.length === 0
        ) {
          return [0, 10_000]; // Default range
        }
        return searchParams.compensation;
      }
      case "compensation_rate":
        return searchParams.compensation_rate || [];
      case "category":
        return searchParams.category || [];
      case "work_model":
        return searchParams.work_model || [];
      case "commitment":
        return searchParams.commitment || [];
      case "priority":
        return searchParams.priority || [];
      case "resume":
        return searchParams.resume || [];
      case "posted_date":
        return searchParams.posted_date?.length === 2
          ? {
              from: searchParams.posted_date[0],
              to: searchParams.posted_date[1],
            }
          : undefined;
      default:
        return [];
    }
  };

  return (
    <Popover
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setSelected(null);
        }
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          aria-label={
            activeFilterCount > 0
              ? `Open filters, ${activeFilterCount} active`
              : "Open filters"
          }
          className="relative size-9"
          size="icon"
          variant={activeFilterCount > 0 ? "default" : "secondary"}
        >
          <FilterIcon className="h-4 w-4" />
          {activeFilterCount > 0 ? (
            <span className="-right-1 -top-1 absolute flex size-4 items-center justify-center rounded-full bg-background font-semibold text-[10px] text-foreground ring-1 ring-border">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-1" side="bottom">
        {selectedField ? (
          <FilterDetailPanel
            getFieldValue={getFieldValue}
            onBack={() => setSelected(null)}
            resetField={resetField}
            searchParams={searchParams}
            selectedField={selectedField}
            setSearchParams={setSearchParams}
            userLatitude={userLatitude}
            userLongitude={userLongitude}
          />
        ) : (
          <FilterList
            filterFields={filterFields}
            getFieldValue={getFieldValue}
            onSelect={setSelected}
            resetField={resetField}
            searchParams={searchParams}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
